import { addMonths, format } from 'date-fns';

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;
// Moment's average month length: a 400-year Gregorian cycle has 146097 days and 4800 months
const MONTHS_PER_DAY = 4800 / 146097;
const DAYS_PER_MONTH = 146097 / 4800;

/**
 * Normalizes string | Date input; throws on invalid dates so callers'
 * catch blocks return their fallback strings ('Unknown', 'now', ...). This
 * intentionally replaces moment's literal 'Invalid date'/'NaNy' outputs for
 * unparseable input.
 *
 * Note: date-only strings ('2024-01-15') parse as UTC midnight here, unlike
 * the previous moment-based parsing which used LOCAL midnight — pass full ISO
 * datetime strings or Date objects (all current callers do).
 */
function toValidDate(dateValue: string | Date): Date {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError('Invalid date');
  }
  return date;
}

/**
 * Whole calendar months between earlier and later (earlier <= later) plus the
 * millisecond remainder past the last whole month. Mirrors moment's
 * momentsDifference, whose calendar-aware month count feeds fromNow();
 * date-fns addMonths clamps end-of-month exactly like moment's add('M').
 */
function calendarMonthDiff(
  earlier: Date,
  later: Date,
): { wholeMonths: number; leftoverMs: number } {
  let wholeMonths =
    (later.getFullYear() - earlier.getFullYear()) * 12 + (later.getMonth() - earlier.getMonth());
  if (addMonths(earlier, wholeMonths).getTime() > later.getTime()) {
    wholeMonths -= 1;
  }
  return {
    wholeMonths,
    leftoverMs: later.getTime() - addMonths(earlier, wholeMonths).getTime(),
  };
}

/**
 * Picks the short relative-time token for a date pair (earlier <= later).
 * Replicates moment's fromNow() with the custom short locale previously
 * configured via moment.updateLocale (s: '%ds', m: '1m', mm: '%dm', h: '1h',
 * hh: '%dh', d: '1d', dd: '%dd', M: '1mo', MM: '%dmo', y: '1y', yy: '%dy'):
 * moment builds the duration from whole calendar months + a millisecond
 * remainder, converts the month part to days via the Gregorian average, and
 * applies its default relativeTime thresholds (ss/s: 44s, m: 45, h: 22,
 * d: 26, M: 11) with Math.round at each unit.
 */
function getRelativeToken(earlier: Date, later: Date): string {
  const { wholeMonths, leftoverMs } = calendarMonthDiff(earlier, later);
  const monthsAsDays = Math.round(wholeMonths * DAYS_PER_MONTH);
  const seconds = Math.round(monthsAsDays * 86_400 + leftoverMs / MS_PER_SECOND);
  const minutes = Math.round(monthsAsDays * 1_440 + leftoverMs / MS_PER_MINUTE);
  const hours = Math.round(monthsAsDays * 24 + leftoverMs / MS_PER_HOUR);
  const days = Math.round(monthsAsDays + leftoverMs / MS_PER_DAY);
  const monthsExact = wholeMonths + (leftoverMs / MS_PER_DAY) * MONTHS_PER_DAY;
  const months = Math.round(monthsExact);
  const years = Math.round(monthsExact / 12);

  if (seconds <= 44) {
    // moment's substituteTimeAgo coerces 0 to 1, so sub-500ms diffs render '1s'
    return `${seconds || 1}s`;
  }
  if (minutes <= 1) {
    return '1m';
  }
  if (minutes < 45) {
    return `${minutes}m`;
  }
  if (hours <= 1) {
    return '1h';
  }
  if (hours < 22) {
    return `${hours}h`;
  }
  if (days <= 1) {
    return '1d';
  }
  if (days < 26) {
    return `${days}d`;
  }
  if (months <= 1) {
    return '1mo';
  }
  if (months < 11) {
    return `${months}mo`;
  }
  if (years <= 1) {
    return '1y';
  }
  return `${years}y`;
}

/**
 * Formats a date/time relative to now (e.g., "2h ago", "5m ago")
 */
export function formatRelativeTime(dateValue: string | Date): string {
  try {
    const date = toValidDate(dateValue);
    // Math.floor matches moment .diff() truncation for past dates
    const diffMs = Date.now() - date.getTime();
    const diffHours = Math.floor(diffMs / MS_PER_HOUR);

    if (diffHours < 1) {
      // Less than 1 hour, show minutes
      const diffMinutes = Math.floor(diffMs / MS_PER_MINUTE);
      if (diffMinutes < 1) {
        return 'just now';
      }
      return `${diffMinutes}m ago`;
    } else if (diffHours < 24) {
      // Less than 24 hours, show hours
      return `${diffHours}h ago`;
    } else if (diffHours < 48) {
      // Less than 48 hours, show "yesterday"
      return 'yesterday';
    } else {
      // More than 48 hours, show date
      return format(date, 'MMM d');
    }
  } catch (e) {
    console.error('Error formatting time', e);
    return 'just now';
  }
}

/**
 * Formats a date/time as an exact timestamp (e.g., "July 15, 2023 at 2:30 PM")
 */
export function formatExactTimestamp(dateValue: string | Date): string {
  try {
    return format(toValidDate(dateValue), "MMMM d, yyyy 'at' h:mm a");
  } catch (e) {
    console.error('Error formatting exact timestamp', e);
    return 'Unknown date';
  }
}

type RelativeTimeOptions = {
  /**
   * The caller's clock tick, in epoch ms, not a historical reference. Components pass it because
   * React Compiler memoizes the call on its arguments, so a call without one keeps the string it
   * rendered first. The string is measured from the later of the tick and the current time: a
   * date that arrives between ticks (a realtime update) never reads as in the future or as older
   * than it is.
   */
  now?: number;
};

type FromNowOptions = RelativeTimeOptions & {
  /** Returns the bare token ("5h") instead of "5h ago" or "in 5h". */
  withoutSuffix?: boolean;
};

/**
 * Returns a short relative time string with a suffix by default
 * (e.g., "3m ago", "5h ago", "1d ago", "in 5m"); withoutSuffix returns the bare token
 */
export function getFromNow(
  dateValue: string | Date,
  { withoutSuffix = false, now = Date.now() }: FromNowOptions = {},
): string {
  try {
    const date = toValidDate(dateValue);
    const nowDate = new Date(Math.max(now, Date.now()));
    const isFuture = date.getTime() > nowDate.getTime();
    const token = isFuture ? getRelativeToken(nowDate, date) : getRelativeToken(date, nowDate);
    if (withoutSuffix) {
      return token;
    }
    return isFuture ? `in ${token}` : `${token} ago`;
  } catch (e) {
    console.error('Error getting fromNow string', e);
    return 'Unknown';
  }
}

/**
 * Returns a short relative time for inbox display
 * (e.g., "3m", "2h", "5d", "3mo", "2y")
 */
export function getShortRelativeTime(
  dateValue: string | Date,
  { now = Date.now() }: RelativeTimeOptions = {},
): string {
  try {
    const date = toValidDate(dateValue);
    // Clamp instead of abs: client clock skew can put fresh server timestamps
    // seconds in the future; the old moment path rendered those as 'now'
    const absDiffMs = Math.max(0, Math.max(now, Date.now()) - date.getTime());
    // Math.floor matches moment.duration().asX() truncation
    const minutes = Math.floor(absDiffMs / MS_PER_MINUTE);
    const hours = Math.floor(absDiffMs / MS_PER_HOUR);
    const days = Math.floor(absDiffMs / MS_PER_DAY);
    const monthsExact = (absDiffMs / MS_PER_DAY) * MONTHS_PER_DAY;
    const months = Math.floor(monthsExact);
    const years = Math.floor(monthsExact / 12);

    if (minutes < 1) {
      return 'now';
    }
    if (minutes < 60) {
      return `${minutes}m`;
    }
    if (hours < 24) {
      return `${hours}h`;
    }
    if (days < 30) {
      return `${days}d`;
    }
    if (months === 0) {
      // If it's between 30 days and 1 month, still show days
      return `${days}d`;
    }
    if (years < 1) {
      return `${months}mo`;
    }
    return `${years}y`;
  } catch (e) {
    console.error('Error formatting short relative time', e);
    return 'now';
  }
}

/**
 * date-fns format() with moment's behavior on unparseable input: renders the
 * literal 'Invalid date' instead of throwing a RangeError
 */
export function formatWithPattern(dateValue: string | Date, pattern: string): string {
  try {
    return format(toValidDate(dateValue), pattern);
  } catch {
    return 'Invalid date';
  }
}

const UTC_SHORT_DATE = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});
const UTC_SHORT_DATE_WITH_YEAR = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * Formats a billing date by its UTC day, so a midnight UTC renewal doesn't show as the day before
 * in the Americas: "Oct 20", with the year only when it differs from now's, as in "Jan 3, 2027".
 */
export function formatUtcShortDate(date: Date, now: Date): string {
  const formatter =
    date.getUTCFullYear() === now.getUTCFullYear() ? UTC_SHORT_DATE : UTC_SHORT_DATE_WITH_YEAR;
  return formatter.format(date);
}

/**
 * Formats a date as a short date string (e.g., "Jan 15, 2024").
 * Same caveat as toValidDate: date-only strings ('2024-01-15') parse as UTC
 * midnight, so pass full ISO datetime strings or Date objects.
 */
export function formatShortDate(dateString: string | Date): string {
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return format(date, 'MMM d, yyyy');
  } catch {
    return '';
  }
}
